import Box from '@mui/material/Box'
import cn from 'classnames'
import Button from '../../Common/Button'
import Icon from '../../Common/Icon'
import Text from '../../Common/Text'
import styles from './TasksEmptyState.module.scss'

export interface IProps {
  title?: string
  /** One paragraph per entry, in order. */
  descriptions?: string[]
  /** `lg` for the full My Tasks page; `sm` for the ~350px side panel. */
  size?: 'sm' | 'lg'
  /** The call to action only renders when both the label and the handler are given. */
  actionLabel?: string
  onAction?: () => void
  // Passthrough kept as the literal `data-cy` attribute name (not `dataCy`) so
  // the E2E-selector guard sees the identifier at call sites.
  'data-cy'?: string
}

const NO_DESCRIPTIONS: string[] = []

/** Per-size text roles; the heading level stays semantic for each host. */
const SIZE_TEXT_ROLES = {
  sm: { titleComponent: 'h3', titleVariant: 'sectionTitle', descriptionVariant: 'body' },
  lg: { titleComponent: 'h2', titleVariant: 'pageTitle', descriptionVariant: 'bodyLg' },
} as const

/**
 * The "nothing left to do" state for every task list — the green concentric
 * check, the reassurance copy and an optional add-a-task call to action. Shared
 * so the side panel, the client page's Tasks tab and the My Tasks page can't
 * drift into three different-looking empty states.
 */
const TasksEmptyState = ({
  title = "You're all caught up",
  descriptions = NO_DESCRIPTIONS,
  size = 'sm',
  actionLabel,
  onAction,
  'data-cy': dataCy,
}: IProps) => {
  const isLarge = size === 'lg'
  const { titleComponent, titleVariant, descriptionVariant } = SIZE_TEXT_ROLES[size]

  return (
    <Box className={cn(styles.empty, { [styles.emptyLarge]: isLarge })} data-cy={dataCy}>
      <Box className={styles.icon}>
        {/* The page state still reads a step larger than the panel one, as it always
            has — expressed through the size tokens rather than a per-site width. */}
        <Icon name="check-circle" size={isLarge ? 'lg' : 'md'} />
      </Box>
      <Text
        casing="role"
        component={titleComponent}
        variant={titleVariant}
        tone="primary"
        className={styles.title}
      >
        {title}
      </Text>
      {descriptions.map((description) => (
        <Text
          casing="role"
          key={description}
          variant={descriptionVariant}
          tone="secondary"
          className={cn(styles.description, { [styles.descriptionLarge]: isLarge })}
        >
          {description}
        </Text>
      ))}
      {actionLabel && onAction && (
        <Button
          type="button"
          small
          className={cn({ [styles.ctaLarge]: isLarge })}
          onClick={onAction}
        >
          {actionLabel}
        </Button>
      )}
    </Box>
  )
}

export default TasksEmptyState
